import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { useMatch, useNavigate, useSearch } from '@tanstack/react-router'
import { Dialog as DialogPrimitive } from 'radix-ui'
import { useRef, useState } from 'react'
import {
  bookingDetailQuery,
  bookingsQuery,
  BookingStatusChip,
  isRenderableBookingAction,
} from '@/entities/booking'
import { ConversationThread } from '@/entities/conversation'
import { AiDraftReview } from '@/features/ai-draft-review'
import { MessageComposer, PendingMessages, useMessageSend } from '@/features/message-send'
import type { BookingDetail } from '@/shared/api'
import { guestDisplayName } from '@/shared/lib/format'
import { useThemeStore } from '@/shared/lib/theme'
import { EmptyState, Icon, Skeleton, WidgetError } from '@/shared/ui'
import { findNeighbours } from '../lib/neighbours'
import { BookingActionsCard } from './BookingActionsCard'
import { BookingClientCard } from './BookingClientCard'
import { BookingDetailsCard } from './BookingDetailsCard'
import { BookingEditDialog } from './BookingEditDialog'
import { BookingFlowCard } from './BookingFlowCard'
import { BookingHistoryCard } from './BookingHistoryCard'
import { BookingInterventionCard } from './BookingInterventionCard'
import { BookingNotesCard } from './BookingNotesCard'
import { BookingPlatformCard } from './BookingPlatformCard'
import s from './BookingModal.module.css'
import { BookingPaymentsCard } from './BookingPaymentsCard'
import { BookingStayCard } from './BookingStayCard'
import { GuestScreenButton } from './GuestScreenButton'

/**
 * Карточка брони — полноэкранный слой поверх любого экрана (prototype/Prototype.dc.html:2321).
 *
 * Здесь — слой, скрим, шапка, открытие и закрытие, сборка левой колонки из секций и панели чата
 * справа. Геометрия секций — `docs/reference/phase-2-mockup.md` §3.
 *
 * **Не `Dialog` из кита:** тот написан под «Новую бронь» (центрирование, radius 16,
 * `--shadow-modal`, скрим с blur), здесь слой во весь экран без скругления, скрим `--glass-bg`.
 * Общее — только поведение, и его даёт голый Radix: фокус-трап, Escape, блокировка скролла,
 * `role="dialog"`, `aria-labelledby`.
 *
 * `aria-modal` Radix НЕ ставит (проверено зондом на живой сборке), и это не упущение: вместо него
 * `hideOthers` вешает `aria-hidden="true"` на соседей слоя — механизм сильнее, а не слабее.
 *
 * ⚠️ Единственное место, где Radix взят вне `shared/ui`. Конвенция («Radix заперт в обёртках кита»)
 * существует, чтобы не обходить ГОТОВУЮ обёртку; готовой под этот слой нет, а заводить её ради
 * единственного потребителя значило бы положить виджет в `shared`. Отступление одноразовое.
 */
export function BookingModal() {
  // `booking` объявлен на КОРНЕВОМ роуте (app/router/search.ts) — дочерние роуты наследуют его
  // search, поэтому из оболочки он виден по её собственному id.
  const { booking: bookingId } = useSearch({ from: '/shell' })
  const navigate = useNavigate()
  const open = Boolean(bookingId)

  /*
   * Куда вернуть фокус при закрытии. Radix этого не сделает: его дефолт —
   * `context.triggerRef.current?.focus()` (index.mjs:148-151), а `triggerRef` заполняет только
   * `Dialog.Trigger`, которого здесь нет — модалку открывает search-параметр. Без этого фокус
   * улетает на `<body>`, и пришедший с клавиатуры теряет строку таблицы.
   *
   * ⚠️ Ловим открывший элемент синхронно во время РЕНДЕРА — до всех эффектов, включая монтирование
   * `FocusScope`, который уже увёл бы фокус на слой. Тот же приём, что в `shared/ui/dialog`.
   *
   * При открытии по прямой ссылке открывшего элемента нет — `openerRef` остаётся пустым, и фокус
   * после закрытия честно остаётся ни на чём.
   */
  const openerRef = useRef<HTMLElement | null>(null)
  const wasOpenRef = useRef(open)
  if (open !== wasOpenRef.current) {
    if (open) {
      openerRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null
    }
    wasOpenRef.current = open
  }

  /*
   * Закрытие — ТОЛЬКО функцией search. Голый объект стёр бы `status`/`sort` из URL: TanStack
   * Router не сливает search, а ЗАМЕНЯЕТ строку запроса целиком (правило записано в
   * app/router/search.ts). Человек, открывший бронь из отфильтрованного списка, закрыл бы её
   * и увидел список, сброшенный на «Все».
   */
  const close = () => void navigate({ to: '.', search: (prev) => ({ ...prev, booking: undefined }) })

  return (
    <DialogPrimitive.Root
      open={open}
      onOpenChange={(next) => {
        // Radix зовёт это и на Escape, и на клик вне слоя. Открыть карточку он не может —
        // открывает её только URL.
        if (!next) close()
      }}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className={s.scrim}>
          <DialogPrimitive.Content
            className={s.layer}
            /*
             * Описания у диалога нет, а Radix безусловно вешает на Content `aria-describedby` с id
             * своего `Description` (index.mjs:224) — ссылка указывала бы в несуществующий элемент.
             * `undefined` в пропсе перекрывает её: спред пропсов идёт после (index.mjs:227).
             */
            aria-describedby={undefined}
            /*
             * Фокус при открытии — на САМУ панель, а не на первый tabbable: дефолт Radix
             * сфокусировал бы первую кнопку, а первая здесь «← Брони», которая карточку ЗАКРЫВАЕТ.
             * Панель фокусируема, `tabIndex: -1` ставит сам Radix. Тот же приём, что в
             * `shared/ui/popover` (HANDOFF.md §5).
             */
            onOpenAutoFocus={(event) => {
              event.preventDefault()
              if (event.currentTarget instanceof HTMLElement) event.currentTarget.focus()
            }}
            onCloseAutoFocus={(event) => {
              event.preventDefault()
              openerRef.current?.focus()
            }}
          >
            {bookingId && <BookingModalLayer bookingId={bookingId} onClose={close} />}
          </DialogPrimitive.Content>
        </DialogPrimitive.Overlay>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}

/**
 * Содержимое слоя. Отдельным компонентом, чтобы его хуки (запрос детали, чтение списка из кеша)
 * жили только пока слой открыт: при закрытом `Root` Radix не монтирует детей вовсе.
 */
function BookingModalLayer({ bookingId, onClose }: { bookingId: string; onClose: () => void }) {
  const { data, error, refetch } = useQuery(bookingDetailQuery(bookingId))
  // Пустые плитки-действия (сервер прислал только kind/id — напр. авто-шаг движка) не считаем
  // действиями: секция «Требует вашего действия» показывается, только если есть что показать.
  const visibleActions = data ? data.actionsRequired.filter(isRenderableBookingAction) : []

  return (
    <>
      <BookingModalHeader booking={data} error={error} bookingId={bookingId} onClose={onClose} />

      <div className={s.body}>
        {/*
         * ⚠️ КЛЮЧ — БРОНЬ. Окна, подтверждения и отказы секций принадлежат ей, и прокрутка карточки
         * тоже, а модалка при листании к соседу из кеша не размонтируется: без ключа окно, открытое
         * на Б, после «назад» отправит запрос в А — платёж, действие, вмешательство, — а соседняя
         * бронь откроется на глубине прежней. Сосед не из кеша и так пересоздаёт секции через
         * скелетон; ключ делает оба пути одинаковыми.
         */}
        <div key={bookingId} className={s.main}>
          <div className={s.grid}>
            {/*
             * ⚠️ Данные → ошибка → скелетон, именно в таком порядке (HANDOFF.md §5): упавший ФОНОВЫЙ
             * перезапрос оставляет `data` целой и выставляет `error`, и ветка `error ? …` спрятала
             * бы живую карточку от вернувшегося во вкладку. Ошибку показываем, только если показать
             * нечего.
             */}
            {data ? (
              <>
                {/*
                 * ⚠️ ПОМЕТКА О ЧАСТИЧНОМ ИМПОРТЕ СТОИТ ПЕРВОЙ, ДО ВСЕХ ДАННЫХ, И ЭТО ЕЁ СМЫСЛ: она
                 * меняет чтение КАЖДОГО поля ниже — без неё пустое поле читается как «этого нет», с
                 * ней как «мы не знаем». Прочитанная после данных, она опоздала бы.
                 *
                 * ⚠️ И ПУСТОТА — НЕ ЕДИНСТВЕННЫЙ СЛУЧАЙ: итог брони едет обычным числом, а не
                 * `optional`, и неприсланную сумму транспорт домена транслирует НУЛЁМ — «0 ₽» в
                 * итоге неотличим от настоящего нуля ничем, кроме этой строки. Деньги ИСТОЧНИКА
                 * такой беды лишены — там отсутствие выражено отсутствием строки, — но итог брони
                 * приходит не оттуда.
                 *
                 * Фраза приходит готовой: причина неполноты принадлежит тому импорту, которым запись
                 * завели, и своей копии у нас нет намеренно.
                 */}
                {data.partialNote && (
                  <p className={s.partialNote} role="note">
                    {data.partialNote}
                  </p>
                )}

                <BookingStayCard booking={data} />

                {/*
                 * Секции нет, когда действий нет: пустая рамка с заголовком «Требует вашего
                 * действия» утверждала бы, что действие есть. Действием считаем только НЕпустое —
                 * плитку, где сервер прислал один kind/id, не рисуем.
                 */}
                {visibleActions.length > 0 && (
                  <BookingActionsCard bookingId={data.id} actions={visibleActions} />
                )}

                {/*
                 * Примечание к брони — ПОСЛЕ срочного, ДО механики сценария: «Требует вашего
                 * действия» единственная секция с кнопками, и задвинуть её текстом переменной длины
                 * значило бы спрятать срочное за контекстом.
                 *
                 * ⚠️ Права `bookings.guests.view` здесь НЕТ и быть не должно: это не ПДн гостя.
                 */}
                {data.internalNotes && <BookingNotesCard notes={data.internalNotes} />}

                {/* Тот же принцип: ход брони без единого этапа — это пустая рамка, а не дорожка. */}
                {data.flow.steps.length > 0 && <BookingFlowCard flow={data.flow} />}

                {/*
                 * Сразу за ходом брони: сначала где бронь стоит, потом чем в это вмешаться.
                 *
                 * ⚠️ Осознанное отступление от макета: там «Вмешаться в эту бронь» — подзаголовок
                 * ВНУТРИ секции хода брони (Prototype.dc.html:2338). Здесь своя секция, потому что
                 * дорожка хода рисуется только при наличии сегментов, а правки нужны и без них —
                 * вложив, мы прятали бы пять операций по причине, к ним не относящейся.
                 */}
                {data.interventions.length > 0 && (
                  <BookingInterventionCard bookingId={data.id} interventions={data.interventions} />
                )}

                {/*
                 * Сразу под правками: сначала «что можно сделать», потом «что уже делали». Пустую
                 * историю не рисуем; обещание «причина запишется в историю» от этого не повисает —
                 * секция появляется тем самым действием, которое её создало.
                 */}
                {data.history.length > 0 && <BookingHistoryCard entries={data.history} />}

                {/*
                 * Платежи и клиент — единственная пара секций, делящая строку пополам
                 * (`grid-column: auto` у обеих). Остальные — во всю ширину.
                 *
                 * Условий на пустоту здесь НЕТ: `payments` и `client` в контракте ОБЯЗАТЕЛЬНЫ, и
                 * секция без единой позиции остаётся честной — итог и прогресс-бар осмысленны, как и
                 * статистика с ссылкой в профиль. Это не «пустая рамка», как было бы у флоу без
                 * сегментов.
                 *
                 * ⚠️ ИСКЛЮЧЕНИЕ ОДНО — ЗАКРЫТЫЙ ПЕРИОД, и оно не про пустоту: этих сущностей у него
                 * НЕ СУЩЕСТВУЕТ. Секция платежей показала бы прогресс оплаты обслуживания, а
                 * карточка гостя — профиль человека, которого нет.
                 *
                 * `BookingClientCard` получает только `clientId` и читает профиль сам, поэтому у
                 * него СВОИ загрузка и ошибка: бронь пришла, а профиль ещё нет — нормальная секунда.
                 * Имя гостя он ПОКАЗЫВАЕТ, и это не дубль шапки: там имя ЭТОЙ брони, здесь — то,
                 * которое компания считает основным для гостя. Они могут расходиться (имена
                 * копятся, а не заменяются) или профиль может не иметь имени вовсе.
                 */}
                {data.status !== 'blocked' && (
                  <>
                    <BookingPaymentsCard
                      bookingId={data.id}
                      bookingCode={data.code}
                      payments={data.payments}
                    />
                    <BookingClientCard clientId={data.clientId} />
                  </>
                )}

                {/*
                 * Данные источника — СРАЗУ ПОСЛЕ платежей: рядом с нашими деньгами видно, что чужие
                 * — чужие. Дальше атрибуция «это её слова» теряет собеседника, ближе — секции
                 * сливаются.
                 *
                 * Секции нет у двух видов записей: у закрытого периода источника не существует, а у
                 * брони без записанного происхождения домен опускает `source` целиком. Проверять
                 * статус не нужно — сервер просто не присылает поле.
                 */}
                {data.platformReport && <BookingPlatformCard report={data.platformReport} />}

                {/* `details` тоже обязателен, но пустой список — это буквально ноль строк:
                    рамка с заголовком и пустотой внутри. Такую не рисуем. */}
                {data.details.length > 0 && <BookingDetailsCard details={data.details} />}
              </>
            ) : error ? (
              <div className={s.errorSlot}>
                <WidgetError error={error} retry={() => void refetch()} />
              </div>
            ) : (
              <div className={s.sectionSkeleton}>
                <Skeleton width="42%" height={27} radius={6} />
                <Skeleton width="100%" height={14} radius={4} />
                <Skeleton width="68%" height={14} radius={4} />
              </div>
            )}
          </div>
        </div>

        {/*
         * Панель чата.
         *
         * `conversationId` в контракте `string | null`: `null` значит «переписки с этим гостем ещё
         * нет» — честное пустое состояние. Пока бронь не загрузилась, мы этого не знаем, поэтому
         * скелетон, а не «переписки нет».
         *
         * ⚠️ **Но если загрузка УПАЛА — скелетона тоже быть не должно.** Он обещает данные, которые
         * не придут: `conversationId` живёт в самой брони, которой нет. Ошибку карточка показывает
         * ОДИН раз, в теле; здесь молчим.
         */}
        <aside className={s.chat}>
          {data ? (
            data.conversationId ? (
              /*
               * ⚠️ Ключ — бронь, как у секций: канал отправки, фильтр ленты, «ссылка ушла» принадлежат
               * этой переписке, а переписка у брони ровно одна (`prima-inbox`, `schema.sql`:
               * `booking_id` — первичный ключ `conversations`). Стражи смены переписки у хуков панели
               * сбрасывают набранный текст и черновик, но не состояние её компонентов.
               */
              <ChatPanel key={data.id} conversationId={data.conversationId} bookingId={data.id} />
            ) : (
              /*
               * ⚠️ У ЗАКРЫТОГО ПЕРИОДА ГОСТЯ НЕТ, и «с этим гостем ещё не переписывались» обещало
               * бы, что переписка когда-нибудь появится. Не появится: писать некому — период
               * закрыт обслуживанием, а не занят человеком.
               */
              <EmptyState
                className={s.chatEmpty}
                icon="email"
                title="Переписки нет"
                description={
                  data.status === 'blocked'
                    ? 'Это закрытый период — гостя, с которым можно переписываться, у него нет.'
                    : 'С этим гостем ещё не переписывались.'
                }
                tint="var(--ink-3)"
              />
            )
          ) : error ? null : (
            <div className={s.chatSkeleton}>
              <Skeleton width={36} height={36} radius={10} />
              <div className={s.chatSkeletonText}>
                <Skeleton width="54%" height={13} radius={4} />
                <Skeleton width="76%" height={11} radius={4} />
              </div>
            </div>
          )}
        </aside>
      </div>
    </>
  )
}

/**
 * Панель чата целиком: лента + её низ — очередь отправки, композер, карточка черновика ИИ.
 *
 * **Собирается здесь, а не внутри треда, по требованию FSD:** `ConversationThread` лежит в
 * `entities`, композер и черновик — в `features`, импорт вверх запрещён. Тред принимает их слотами,
 * составляет общий родитель.
 *
 * **Композер и очередь отправки — один хук на тред** (`useMessageSend`), и он кормит ОБА слота:
 * пузыри неотправленного едут внутрь скролла (`feedEnd`), поле ввода — под него (`footer`), а
 * «Править» у черновика кладёт текст в то же поле. Хук здесь, потому что здесь общий предок:
 * фичи — соседи по слою и друг друга не видят.
 *
 * Отдельный компонент, а не тело `BookingModalLayer`: его хуки должны монтироваться, только когда у
 * брони есть переписка, и умирать вместе с ней.
 */
function ChatPanel({ conversationId, bookingId }: { conversationId: string; bookingId: string }) {
  const send = useMessageSend(conversationId)

  return (
    <ConversationThread
      conversationId={conversationId}
      // Рядом с перепиской, а не карточкой слева: вопрос «что у гостя на экране» задают, глядя в
      // диалог с ним. Слот аддитивный — «Открыть в мессенджере» остаётся на месте.
      headerExtra={<GuestScreenButton bookingId={bookingId} />}
      feedEnd={
        <>
          {/* Черновик — последним элементом ленты, наши свежие сообщения под ним. ⚠️ Адреса в
              макете у этого порядка НЕТ: карточки черновика в нынешнем прототипе не осталось
              (`sendDraft` объявлен и в разметке не используется), так что решение наше. */}
          <AiDraftReview conversationId={conversationId} onEdit={send.edit} />
          <PendingMessages send={send} />
        </>
      }
      footer={<MessageComposer conversationId={conversationId} send={send} />}
    />
  )
}

type BookingModalHeaderProps = {
  /** `undefined`, пока деталь не загрузилась: чип, код и бейдж рисуем только по данным. */
  booking: BookingDetail | undefined
  /**
   * Ошибка загрузки. Шапке она нужна не чтобы что-то показать, а чтобы ПЕРЕСТАТЬ показывать
   * скелетон: без неё он пульсировал бы вечно рядом с телом, которое уже говорит «Сервер не
   * смог ответить».
   */
  error: unknown
  bookingId: string
  onClose: () => void
}

function BookingModalHeader({ booking, error, bookingId, onClose }: BookingModalHeaderProps) {
  const navigate = useNavigate()
  const toggleTheme = useThemeStore((state) => state.toggle)
  const neighbours = useNeighbours(bookingId)

  const goTo = (id: string) =>
    void navigate({ to: '.', search: (prev) => ({ ...prev, booking: id }) })

  /**
   * «След.» на краю ЗАГРУЖЕННОГО списка догружает следующую страницу и уходит на первую бронь
   * из неё, а не гаснет. Погасшая кнопка — это утверждение «дальше броней нет», и на 12-й броне
   * из 788 оно было бы ложью.
   *
   * Подписка выключена (`enabled: false`), но `fetchNextPage` на ней работает — проверено зондом
   * на живой библиотеке: `enabled` гасит только АВТОМАТИЧЕСКИЕ запросы наблюдателя, а явный
   * вызов проходит, и данные приезжают в тот же кеш, что читает страница под карточкой.
   */
  const goNext = async () => {
    if (!neighbours) return
    if (neighbours.next) return goTo(neighbours.next)
    if (!neighbours.hasMore) return

    const { data } = await neighbours.loadMore()
    const ids = data?.pages.flatMap((page) => page.items.map((item) => item.id)) ?? []
    const next = findNeighbours(ids, bookingId)?.next
    if (next) goTo(next)
  }

  return (
    <header className={s.header}>
      <div className={s.headerLeft}>
        <button type="button" className={s.back} title="Назад" onClick={onClose}>
          <Icon name="chevron-left" size={15} strokeWidth={2} />
          Брони
        </button>
        <span className={s.divider} aria-hidden="true" />

        {booking ? (
          <>
            {/* Это ДОСТУПНОЕ ИМЯ модалки — то, что скринридер произносит при её открытии. Имя гостя
                бывает пустым (§3), и тогда здесь оставалось «Бронь bk_… — » с висящим тире.
                ⚠️ У блокировки гостя нет вовсе, и произносить «Без имени» тут значило бы объявить
                несуществующего человека: называем период тем, чем он закрыт. */}
            <DialogPrimitive.Title className={s.srOnly}>
              {booking.status === 'blocked'
                ? `Закрытый период ${booking.code} — ${booking.blockReasonLabel ?? booking.statusLabel}`
                : `Бронь ${booking.code} — ${guestDisplayName(booking.guest.name)}`}
            </DialogPrimitive.Title>
            <BookingStatusChip status={booking.status} label={booking.statusLabel} size="md" />
            <span className={s.code}>{booking.code}</span>
            {/* `highlight` в контракте `null`, когда бейджа нет, — рисуем строго по данным. */}
            {booking.highlight && (
              <span className={s.highlight}>
                <span className={s.highlightDot} aria-hidden="true" />
                {booking.highlight}
              </span>
            )}
          </>
        ) : (
          <>
            <DialogPrimitive.Title className={s.srOnly}>Карточка брони</DialogPrimitive.Title>
            {/*
             * Скелетон — только пока данные ЕЩЁ едут. Если загрузка упала, он превращается
             * в ложь: он обещает, что данные вот-вот придут, а они не придут — и пульсирует
             * бесконечно (`animation: skel … infinite`) рядом с телом карточки, которое уже
             * говорит «Сервер не смог ответить». Правило «данные → ошибка → скелетон» соблюли
             * в теле и забыли здесь.
             *
             * Своей ошибки шапка не показывает намеренно: она одна на карточку, и показывает
             * её тело — там же и «Повторить». Дублировать не надо, надо просто замолчать.
             */}
            {!error && <Skeleton width={132} height={26} radius={999} />}
          </>
        )}
      </div>

      <div className={s.headerRight}>
        {/*
         * «Изменить» — в ШАПКЕ, а не в секции примечания, и это не вкусовщина. Правка заводит
         * примечание в том числе там, где его ещё НЕТ, — а секция примечания у такой брони не
         * рисуется вовсе. Кнопка внутри неё была бы недостижима ровно в том случае, ради которого
         * чаще всего и открывают правку.
         *
         * Есть только при загруженной брони: пока данных нет, править нечего, а версии для
         * `If-Match` не существует.
         *
         * `title` у кнопки НЕ ставим: подпись видимая, и подсказка повторяла бы её дословно.
         * У соседей-иконок он есть ровно потому, что видимой подписи у них нет.
         */}
        {booking && <BookingEditControl key={booking.id} booking={booking} />}

        {/*
         * Пара «пред/след» — только когда список броней действительно есть в кеше и наша бронь
         * в нём найдена. Открыли карточку с шахматки, задач или чатов — соседей взять неоткуда,
         * и пары нет вовсе. Мёртвых кнопок в проде не рисуем.
         */}
        {neighbours && (
          <div className={s.pager}>
            <button
              type="button"
              className={s.pagerButton}
              title="Пред."
              aria-label="Предыдущая бронь"
              disabled={!neighbours.prev}
              onClick={() => neighbours.prev && goTo(neighbours.prev)}
            >
              <Icon name="chevron-left" size={14} strokeWidth={2} />
            </button>
            <button
              type="button"
              className={s.pagerButton}
              title="След."
              aria-label="Следующая бронь"
              // Гаснет, только когда броней и правда больше нет: ни в загруженном списке,
              // ни на сервере. Иначе — догрузит страницу, см. `goNext`.
              disabled={(!neighbours.next && !neighbours.hasMore) || neighbours.isLoadingMore}
              onClick={() => void goNext()}
            >
              <Icon name="chevron-right" size={14} strokeWidth={2} />
            </button>
          </div>
        )}

        {/*
         * Кнопка темы своя, а не `ThemeToggle`/`IconButton` из кита: тот 34×34, прозрачный,
         * без рамки (снят с иконок шапки ПРИЛОЖЕНИЯ, prototype:176-211), а здесь 32×32 с
         * рамкой `--line` и фоном `--panel` — как соседние кнопки этой шапки. Перекрывать
         * `IconButton` классом снаружи значило бы завести два источника истины об одной кнопке
         * (об этом прямо сказано в `shared/ui/icon-button/IconButton.module.css`).
         *
         * ⚠️ Кнопка «Редактировать» стоит выше, хотя формы редактирования в макете НЕТ: `PATCH`
         * сведён с тем, что домен правит на самом деле, и форма собрана из кита по образцу «Новой
         * брони». Отсутствие макета здесь не довод против кнопки — довод за то, чтобы не
         * изобретать ей собственный вид.
         */}
        <button
          type="button"
          className={s.headButton}
          title="Тема"
          aria-label="Сменить тему"
          onClick={toggleTheme}
        >
          <Icon name="theme" size={15} strokeWidth={1.8} />
        </button>

        <button
          type="button"
          className={`${s.headButton} ${s.close}`}
          title="Закрыть"
          aria-label="Закрыть"
          onClick={onClose}
        >
          <Icon name="close" size={15} strokeWidth={2} />
        </button>
      </div>

    </header>
  )
}

/**
 * «Изменить» вместе с окном — отдельным компонентом, чтобы шапка поставила ему ключ БРОНИ: окно
 * открывают на одной брони, а «назад» браузера меняет её под открытым окном. Ключ на всю шапку
 * пересоздал бы и «След.»: листающий с клавиатуры терял бы фокус на каждом шаге.
 */
function BookingEditControl({ booking }: { booking: BookingDetail }) {
  const [editing, setEditing] = useState(false)

  return (
    <>
      <button type="button" className={s.editButton} onClick={() => setEditing(true)}>
        Изменить
      </button>
      {/*
       * Окно монтируется ПО ФАКТУ ОТКРЫТИЯ — так форма получает свежее состояние на каждом
       * открытии: `open` управляет только содержимым Radix, а `useState` окна живёт ВЫШЕ него и
       * закрытие переживает.
       *
       * ⚠️ `key={booking.version}` тут НЕ ГОДИТСЯ, хотя и выглядит как «начальные значения
       * снимаются заново». Версия при отмене не меняется — брошенный черновик и плашка ошибки
       * прошлой попытки дожили бы до следующего открытия. И наоборот: версия меняется при
       * ПЕРЕЧИТЫВАНИИ карточки, окно пересоздалось бы, а набранный текст молча заменился бы чужим
       * вместе с «замороженной» версией — тем же ключом отменялась бы защита, ради которой он и
       * ставится.
       */}
      {editing && <BookingEditDialog booking={booking} open onOpenChange={setEditing} />}
    </>
  )
}

/**
 * Соседи текущей брони — из УЖЕ ЗАГРУЖЕННОГО списка в кеше Query. Своей ручки «соседняя бронь»
 * в контракте нет, и выдумывать её нельзя. Карточка открывается поверх экрана «Брони», а он
 * свой список держит. Открыли с шахматки, задач или чатов — соседей взять неоткуда, и пары
 * кнопок не будет вовсе. Мёртвых кнопок в проде не рисуем.
 *
 * Фильтр берём у роута `/bookings`, а не у себя: карточка живёт в оболочке и висит над любым
 * экраном, но список есть только под экраном «Брони». `useMatch({shouldThrow:false})` вернёт
 * `undefined`, когда этот роут не активен, — тогда соседей нет по определению.
 *
 * **Читаем кеш ПОДПИСКОЙ, а не `getQueryData`.** `getQueryData` — одноразовое чтение в момент
 * рендера, оно не подписывает компонент на приход данных, и ссылка на бронь ломается тихо: при
 * открытии по ПРЯМОЙ ССЫЛКЕ (`/bookings?booking=…` — а ею и делятся, ради того карточка и живёт в
 * search-параметре) список ещё летит, чтение возвращает `undefined`, и шапка больше никогда не
 * перечитывает — на список в ней ничто не подписано. Пара «пред/след» не появится уже никогда.
 *
 * `enabled: false` — именно то, что нужно: наблюдатель **подписывается** на кеш и отдаёт данные,
 * когда они там появятся, но **сам запрос не инициирует**. Список грузит страница под нами;
 * дублировать её запрос мы не хотим, а вот узнать о нём — обязаны.
 */
function useNeighbours(bookingId: string) {
  // Тот же id роута, что у страницы броней: `shellRoute` (id 'shell') + '/bookings'.
  // В самом URL сегмента `/shell` нет — см. pages/bookings/ui/BookingsPage.tsx.
  const bookingsMatch = useMatch({ from: '/shell/bookings', shouldThrow: false })

  /*
   * В ключ кеша идут РОВНО `status` и `sort` — то же, что кладёт туда сам экран «Брони».
   *
   * Передать `bookingsMatch.search` целиком нельзя, хотя соблазн велик: там лежит ещё и `booking`
   * (карточка живёт в search-параметре корневого роута, и дочерние его наследуют). Он уехал бы
   * в `query` запроса, а значит и в ключ — ключ разошёлся бы с ключом страницы, и подписка
   * смотрела бы в пустоту. Проверено: именно так эта починка и не сработала с первого раза.
   */
  const filter = {
    status: bookingsMatch?.search.status ?? 'all',
    sort: bookingsMatch?.search.sort ?? 'new',
  } as const

  // Хук зовётся БЕЗУСЛОВНО — правила хуков. Когда карточка открыта не над списком, подписка
  // указывает в ключ, которого в кеше нет, отдаёт `undefined`, и ниже мы вернём `null`.
  const {
    data: list,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({ ...bookingsQuery(filter), enabled: false })

  if (!bookingsMatch || !list) return null

  const ids = list.pages.flatMap((page) => page.items.map((item) => item.id))
  const found = findNeighbours(ids, bookingId)
  if (!found) return null

  return {
    ...found,
    /**
     * Загружен не весь список: за последней загруженной бронью на сервере есть ещё.
     *
     * Без этого «След.» врала на краю ЗАГРУЖЕННОГО: открыта 12-я бронь из 788 — кнопка гаснет,
     * то есть сообщает «дальше броней нет». Их 776. Гасим её только тогда, когда броней и правда
     * больше нет.
     */
    hasMore: hasNextPage,
    loadMore: fetchNextPage,
    isLoadingMore: isFetchingNextPage,
  }
}
